import moment from 'moment'
import type { Moment } from 'moment'

import { BOTTOM_PADDING } from '../utils/layout'

const GUTTER = 0

const WEEKLY_HEADER_HEIGHT = 36
const WEEKLY_BOTTOM_PADDING = 40
const WEEKLY_BODY_BONUS = 10

export type SceneParams = {
  startTime: Moment
  days: number
  width: number
  height: number
  weekly?: boolean
}

export type SnapEdge = {
  startTime: Moment | string | Date
  endTime: Moment | string | Date
}

export type TimeInput = Moment | string | Date | null

export type TimelineScene = {
  readonly startTime: Moment
  readonly days: number
  readonly width: number
  readonly height: number
  readonly contentWidth: number
  readonly contentHeight: number
  readonly secondsInPeriod: number
  readonly gutter: number
  readonly weekly: boolean
  readonly headerHeight: number
  readonly bottomPadding: number

  timeToX(time: TimeInput): number

  widthOf(start: TimeInput, end: TimeInput, currentDate?: Moment | string | Date): number

  xToTime(contentX: number): Moment

  snapToEdges(
    date: Moment,
    edges: ReadonlyArray<SnapEdge>,
    thresholdMinutes?: number,
  ): Moment
}

export function createTimelineScene(params: SceneParams): TimelineScene {
  const startTime = moment(params.startTime)
  const days = params.days ?? 1
  const width = params.width
  const weekly = params.weekly ?? false

  const headerHeight = weekly ? WEEKLY_HEADER_HEIGHT : 0
  const bottomPadding = weekly ? WEEKLY_BOTTOM_PADDING : BOTTOM_PADDING
  const height = weekly
    ? params.height +
      headerHeight +
      (WEEKLY_BOTTOM_PADDING - BOTTOM_PADDING) +
      WEEKLY_BODY_BONUS
    : params.height

  const contentWidth = Math.max(0, params.width - GUTTER * 2)
  const contentHeight = params.height - 54
  const secondsInPeriod = startTime
    .clone()
    .add(days, 'days')
    .diff(startTime, 'seconds')
  const gutter = GUTTER

  const secondWidth = (): number =>
    secondsInPeriod <= 0 ? 0 : contentWidth / secondsInPeriod

  const positionInContent = (time: TimeInput): number => {
    if (time == null || secondsInPeriod <= 0) return 0
    const t = moment(time)
    const clamped = t.isBefore(startTime) ? startTime : t
    const seconds = clamped.diff(startTime, 'seconds')
    return seconds * secondWidth()
  }

  const timeToX = (time: TimeInput): number => gutter + positionInContent(time)

  const widthOf = (
    start: TimeInput,
    end: TimeInput,
    currentDate?: Moment | string | Date,
  ): number => {
    if (start == null || secondsInPeriod <= 0) return 0

    let s = moment(start)
    let e = end ? moment(end) : moment(currentDate).endOf('day')

    if (s.isAfter(e)) return 0
    if (s.isBefore(startTime)) s = startTime.clone()

    const dayEnd = startTime.clone().add(days, 'days')
    if (e.isAfter(dayEnd)) e = dayEnd

    const seconds = e.diff(s, 'seconds')
    return seconds * secondWidth()
  }

  const xToTime = (contentX: number): Moment => {
    if (contentWidth <= 0) return startTime.clone()
    const seconds = (contentX * secondsInPeriod) / contentWidth
    return startTime.clone().add(seconds, 'seconds')
  }

  const snapToEdges = (
    date: Moment,
    edges: ReadonlyArray<SnapEdge>,
    thresholdMinutes = 15,
  ): Moment => {
    if (!edges || edges.length === 0) return date

    let closest: Moment | null = null
    let minDiff = Infinity

    for (const edge of edges) {
      const start = moment(edge.startTime)
      const end = moment(edge.endTime)

      const diffToStart = Math.abs(date.diff(start, 'minutes'))
      if (diffToStart < minDiff && diffToStart <= thresholdMinutes) {
        minDiff = diffToStart
        closest = start
      }

      const diffToEnd = Math.abs(date.diff(end, 'minutes'))
      if (diffToEnd < minDiff && diffToEnd <= thresholdMinutes) {
        minDiff = diffToEnd
        closest = end
      }
    }

    return closest ? closest.clone() : date
  }

  return Object.freeze({
    startTime,
    days,
    width,
    height,
    contentWidth,
    contentHeight,
    secondsInPeriod,
    gutter,
    weekly,
    headerHeight,
    bottomPadding,
    timeToX,
    widthOf,
    xToTime,
    snapToEdges,
  })
}
